import React from 'react';
import classNames from 'classnames';

import { useViewabilityTracking } from '../../../hooks/useViewabilityTracking';
import { useTrackEvent } from '../../../context/AnalyticsContext';
import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks } from '../../../components';

import css from './SearchResultsPanel.module.css';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @param {Object} [props.currentUser] - The current user
 * @param {Function} [props.onToggleWishlist] - Function to call when toggling wishlist
 * @param {Object} [props.searchTrackingData] - Additional tracking data for search context
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    currentUser,
    onToggleWishlist,
    listingTypeParam,
    searchTrackingData = {},
    intl,
  } = props;
  
  const { trackEvent } = useTrackEvent();
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  // Track viewable impressions for search grid listings
  useViewabilityTracking(listings, 'search_grid', searchTrackingData);

  // Handle listing clicks from search grid
  const handleListingClick = (listing, position, eventType = 'click') => {
    const baseEventData = {
      listing_id: listing.id.uuid,
      listing_type: listing.attributes.publicData.listingType,
      click_position: position + 1,
      artist_id: listing.author?.id?.uuid,
      price: listing.attributes.price?.amount,
      currency: listing.attributes.price?.currency,
      source: 'search_grid',
      timestamp: new Date().toISOString(),
      ...searchTrackingData
    };

    if (eventType === 'click') {
      trackEvent('listing_click', baseEventData);
    } else if (eventType === 'detail_view') {
      trackEvent('listing_detail_view', {
        ...baseEventData,
        view_source: 'search_grid_click'
      });
    }
  };

  const classes = classNames(rootClassName || css.root, className);
  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      return '(max-width: 767px) 100vw, (max-width: 1023px) 306px, 348px';
    }
    return '(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 25vw';
  };

  return (
    <div className={classes}>
      <ul className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {listings.map((l, index) => (
          <li key={l.id.uuid} className={css.resultItem}>
            <ListingCard
              className={css.listingCard}
              listing={l}
              position={index}
              source="search_grid"
              trackingData={searchTrackingData}
              renderSizes={cardRenderSizes(isMapVariant)}
              setActiveListing={setActiveListing}
              currentUser={currentUser}
              onToggleWishlist={onToggleWishlist}
              onClick={(listing, position, eventType) =>
                handleListingClick(listing, position, eventType)
              }
              // Add data attributes for intersection observer
              data-listing-id={l.id.uuid}
              data-position={index + 1}
            />
          </li>
        ))}
        {props.children}
      </ul>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
